import React from 'react';

import { Parameters } from '@provider-portal/common/api/gen/DeliveryProviderPortalAuthApi';
import {
  AccountPermissions,
  ProviderPermissions,
} from '@provider-portal/common/services/types/account';
import { cloneDeep } from 'lodash';

import { Facade } from '@bolteu/lattice';

import navConfig from './navBarStructure.json';
import {
  ConfigMenu,
  ConfigMenuCategory,
  ConfigMenuItem,
  isMenuItem,
  isMenuLink,
  MenuEntryPermissions,
} from './navTypes';

export const mergeConfigAndManifest = (
  moduleManifests: Facade.ModuleManifest[],
): ConfigMenuCategory[] => {
  // Clone object
  const mergedConfig: ConfigMenuCategory[] = cloneDeep(navConfig.menu) as ConfigMenuCategory[];

  for (const manifest of moduleManifests) {
    for (const item of manifest.header.menu) {
      const navConfigEntry = mergedConfig.find((entry) => entry.id === item.title);

      if (navConfigEntry && isMenuItem(item)) {
        for (const child of item.children) {
          if (isMenuLink(child)) {
            navConfigEntry.items.push({
              // extract the second entry of the URL link
              id: child.link?.split('/', 3)?.[2] ?? '',
              titleIntlId: child.title,
              href: child.link,
              permission: child.permission as MenuEntryPermissions,
            });
          }
        }
      }
    }
  }

  return mergedConfig;
};

const validatePermissions = (
  activePermissions: ProviderPermissions,
  requiredPermissions: MenuEntryPermissions,
) => {
  const activePermissionObj: Partial<ProviderPermissions> = activePermissions ?? {};

  if (!requiredPermissions?.type || !requiredPermissions.values?.length) {
    return true;
  }

  if (requiredPermissions.type === 'all') {
    return requiredPermissions.values.every((permKey) => activePermissionObj?.[permKey]);
  }
  if (requiredPermissions.type === 'oneOf') {
    return requiredPermissions.values.some((permKey) => activePermissionObj?.[permKey]);
  }

  return true;
};

const filterMenuCategory = (
  menuCategory: ConfigMenuCategory,
  activePermissions: ProviderPermissions,
) => {
  if (menuCategory.hidden) {
    return false;
  }
  if (!validatePermissions(activePermissions, menuCategory.permission)) {
    return false;
  }

  return true;
};

const filterMenuItem = (menuItem: ConfigMenuItem, activePermissions: ProviderPermissions) => {
  if (menuItem.hidden) {
    return false;
  }
  if (!validatePermissions(activePermissions, menuItem.permission)) {
    return false;
  }

  return true;
};

const extendProviderPermissions = (
  providerPermissions: ProviderPermissions,
  targetingParameters: Parameters,
): AccountPermissions => {
  const extendedProviderPermissions = { ...providerPermissions };

  // Enable deductions report
  if (targetingParameters?.delivery_provider_portal_flags?.enable_deductions_report) {
    extendedProviderPermissions.deductionsAccess = true;
  }

  // Enable new merchant venue
  if (targetingParameters?.delivery_provider_portal_flags?.new_merchant_venue_enabled) {
    extendedProviderPermissions.newMerchantVenueAccess = true;
  }

  // Enable Menu Editor in sidebar
  if (targetingParameters?.provider_onboarding?.withMenuEditor) {
    extendedProviderPermissions.menuEditorAccess = true;
  }

  return extendedProviderPermissions;
};

// DPX-2882: temporary solution before proper permissions system to only show specific page(s) for specific role
const filterMenuForExclusivePermissions = (
  menu: ConfigMenuCategory[],
  exclusivePermissions: Partial<ProviderPermissions>,
) => {
  if (!Object.values(exclusivePermissions).filter((value) => value).length) {
    return menu;
  }

  const filteredMenu = [];
  for (const category of menu) {
    const isExclusiveCategory =
      category.permission?.values.some((permission) => exclusivePermissions[permission]) || false;

    if (!isExclusiveCategory) {
      category.items = category.items.filter((menuItem) =>
        menuItem.permission?.values.some((permission) => exclusivePermissions[permission]),
      );
    }

    if (isExclusiveCategory || category.items.length) {
      filteredMenu.push(category);
    }
  }

  return filteredMenu;
};

export const buildMenuFromConfigAndModules = (
  moduleManifests: Facade.ModuleManifest[],
  providerPermissions: ProviderPermissions,
  exclusivePermissions: Partial<ProviderPermissions>,
  targetingParameters: Parameters,
): ConfigMenuCategory[] => {
  const extendedProviderPermissions = extendProviderPermissions(
    providerPermissions,
    targetingParameters,
  );

  const fullMenu = mergeConfigAndManifest(moduleManifests);

  const menuAfterExclusivityFiltering = filterMenuForExclusivePermissions(
    fullMenu,
    exclusivePermissions,
  );

  // Filter menu categories (sections)
  const menuFilteredByPermissions = menuAfterExclusivityFiltering.filter((menuCat) =>
    filterMenuCategory(menuCat, extendedProviderPermissions),
  );

  // Filter menu items (links)
  for (const menuCat of menuFilteredByPermissions) {
    menuCat.items = menuCat.items.filter((menuItem) =>
      filterMenuItem(menuItem, extendedProviderPermissions),
    );
  }

  // Remove empty categories
  return menuFilteredByPermissions.filter((cat) => cat.items.length);
};

export const MenuContext = React.createContext<ConfigMenu>(null);
